 
import React from 'react';
import ComponentWrapper from '../../../components/layout/ComponentWrapper';
import { StudyDesignOverview, StudyDesignArms, StudyCopyBlock } from '../index';
import styled from 'styled-components';
import { DefinitionBlock, Footnote, NotationBlock } from '../../../util/typography';
import { text } from '@storybook/addon-knobs';

const DefinitionBlockStyled = styled.div`
margin-bottom: 8px;
margin-top: -10px`;
const ComponentWrapperStyled = styled(ComponentWrapper)`

 dd{
 [class*='StudyDesign__AndConditionalWrapper-']{
 margin:16px 0;
 }
 [class*='StudyDesign__AndOrConditional-']{
 margin:16px 0;
 }
 }

`;
const OVARIAN = () => (
    
    <ComponentWrapper>
         <StudyCopyBlock 
            title="Study Design"
            description="<p>643 adult patients with platinum-resistant epithelial ovarian, fallopian tube, or primary peritoneal carcinoma with 1 or 2 prior lines of systemic therapy for ovarian carcinoma were evaluated in a phase 3 multicenter, randomized, double-blind, placebo-controlled trial.</p>
            <p>Of the 643 enrolled patients, 466 patients (72%) had tumors expressing PD-L1 with a CPS ≥1.<sup>d</sup></p>"
            footnotes={[{
                label: 'd.', 
                text: 'PD-L1 status was determined using the IHC 22C3 pharmDx assay.' 
            }]}
            definitions='IHC = immunohistochemistry.'
         />
         
         <StudyDesignOverview 
            data={[
                    [
                        {
                            title: 'Patient Eligibility',
                            description: `
                                <ul>
                                    <li>Patients must have received at least 1 line of platinum-based chemotherapy for ovarian cancer with radiographic evidence of disease progression within 6 months after the last dose</li>
                                    <li>Prior therapy with an anti–PD-1/PD-L1 inhibitor, PARP inhibitor, or bevacizumab was permitted</li>
                                    <li>Patients were enrolled regardless of PD-L1 tumor expression status</li>
                                    <li>Patients were ineligible if they had an autoimmune disease that required systemic therapy within 2 years of treatment or a medical condition that required immunosuppression</i>
                                </ul>
                                <h5>Stratification Factors  </h5>
                                <ul>
                                    <li>Investigator decision to use bevacizumab</li>
                                    <li>Geographic region (US or European Union or Rest of World)</li>
                                    <li>PD-L1 status (CPS <1 or CPS 1 to <10 or CPS ≥10)<sup>e</sup></li>
                                </ul>

                            `,
                            type: 'box-gradient',
                            colspan: 2
                        }
                    ],
                    [
                        {
                            type: 'arrow-gray',
                            colspan: 2
                        }
                    ],
                    [
                        {
                            title: 'Randomized<sup>f</sup> <strong>(1:1) (N=643)</strong>',
                            colspan: 2
                        }
                    ],
                ]}
         />
        <ComponentWrapperStyled>
        <StudyDesignArms 
            title="Study Arms" 
            arms={[
                {
                    title: "KEYTRUDA + paclitaxel ± bevacizumab",
                    data: [
                        [
                            {
                                description: [
                                                {
                                                    text: `KEYTRUDA 400 mg IV Q6W (on day 1 of each 6-week cycle)`,
                                                    textCenter:"true",
                                                },
                                                {
                                                    text: `Paclitaxel 80 mg/m<sup>2</sup> (on days 1, 8, and 15 of each 3-week cycle)`,
                                                    conditional: 'and',
                                                    textCenter:"true",
                                                    
                                                }, 
                                                {
                                                    text: 'Bevacizumab<sup>g</sup> 10 mg/kg (on day 1 of a 2-week cycle)',
                                                    conditional: 'AndOr',
                                                    textCenter:"true",
                                                }  
                                                                                           
                                            ],
                                type: 'box-green'
                            }
                        ],
                    ]
                },
                {
                    title: "Placebo + paclitaxel ± bevacizumab",
                    data: [
                        [
                            {
                                description: [
                                                {
                                                    text: `Placebo IV Q6W (on day 1 of each 6-week cycle)`,
                                                    textCenter:"true",
                                                },
                                                {
                                                    text: `Paclitaxel 80 mg/m<sup>2</sup> (on days 1, 8, and 15 of each 3-week cycle)`,
                                                    conditional: 'and',
                                                    textCenter:"true",
                                                },
                                                {
                                                    text: `Bevacizumab<sup>g</sup> 10 mg/kg (on day 1 of a 2-week cycle)`,
                                                    conditional: 'AndOr',
                                                    textCenter:"true",
                                                },
                                                
                                            ],
                                type: 'box-gray'
                            },
                        ],
                    ]
                    
                }
            ]}
             textDescription={<>
                <p>Treatment with KEYTRUDA continued until RECIST v1.1-defined progression of disease, unacceptable toxicity, or a maximum of 24 months.<sup>h</sup></p>
                <p>Assessment of tumor status was performed every 9 weeks for the first year, followed by every 12 weeks thereafter.</p>
             </>}
        />
        </ComponentWrapperStyled>
       <StudyCopyBlock
            title="End Points"
            description={`
                <p><strong>Major efficacy outcome:</strong></p>
                <ul>
                    <li>PFS as assessed by investigator according to RECIST v1.1.</li>
                </ul>
                <p><strong>Additional efficacy outcome:</strong></p>
                <ul>
                    <li>OS</li>
                </ul>
            `}
        />
 
        <StudyCopyBlock
            title="Baseline Characteristics of Patients with CPS ≥1 (n=466)"
            description={`
               <p>Median age (range): 62 years (37-85)
                    <ul>
                        <li>Age ≥65: 38%</li>    
                    </ul>
               </p>
                <p>Race
                    <ul>
                        <li>White: 67%</li>
                        <li>Asian: 20%</li>
                        <li>Missing: 8%</li>
                        <li>Multiple: 3%</li>
                        <li>Black: 2%</li>
                        <li>Native Hawaiian or other Pacific Islander: 0.4%</li>		 
                    </ul>
               </p>
                <p>Ethnicity
                    <ul>
                        <li>Hispanic or Latino: 13%</li>    
                    </ul>
               </p>
                <p>ECOG PS
                    <ul>
                        <li>Score of 0: 55%</li> 
                        <li>Score of 1: 44%</li>    
                    </ul>
               </p>
                <p>Received bevacizumab as study treatment: 73%</p>
                <p>Prior systemic therapy
                    <ul>
                        <li>Bevacizumab: 46%</li>
                        <li>PARP inhibitor: 39%</li>
                        <li>Anti&ndash;PD-1/PD-L1 inhibitor: 3%</li>    
                    </ul>
               </p>
               <p>Number of prior lines of therapy received</p>
                    <ul>
                        <li>1 prior line: 36%</li>
                        <li>2 prior lines: 64%</li>
                    </ul>
                <p>Platinum-free interval following most recent line of therapy</p>
                    <ul>
                        <li><3 months: 47%</li>
                        <li>3 to 6 months: 53%</li>
                    </ul>

            `}
            footnotes={[
                {
                    label: 'e.',
                    text: 'According to the PD-L1 IHC 22C3 pharmDx assay.'
                },
                {
                    label: 'f.',
                    text: "All study medications were administered as an intravenous infusion."
                },
                {
                    label: 'g.',
                    text: "The option to use bevacizumab was by investigator choice prior to randomization."
                },
                {
                    label: 'h.',
                    text: "Administration of KEYRUDA was permitted beyond RECIST-defined disease progression if the patient was clinically stable and considered by the investigator to be deriving clinical benefit."
                }
            ]}
            definitions={"ECOG PS = Eastern Cooperative Oncology Group performance status; IV = intravenous; PARP = poly (ADP-ribose) polymerase; PD-1 = programmed death receptor-1; RECIST v1.1 = Response Evaluation Criteria In Solid Tumors v1.1."}
        />
    </ComponentWrapper>
);
 
export default OVARIAN